import { commonUiRoutes } from '@hds/common-ui-lib/desktop'
import { identityDesktopRoutes } from '@hds/identity-and-authentication-ui-lib/desktop'
import { createRouter, createWebHistory } from 'vue-router'

import ClerkConsole from '@/clerk/ClerkConsole.vue'
import CollectionView from '@/views/CollectionView.vue'
import ComingSoonView from '@/views/ComingSoonView.vue'
import HumansView from '@/views/HumansView.vue'
import LiveSalesView from '@/views/LiveSalesView.vue'
import MachinesView from '@/views/MachinesView.vue'
import { PAGES } from '@/lib/contexts.js'

/**
 * One route per page, built from the contexts rather than written out.
 *
 * The nav model and the router would otherwise be two lists of the same pages,
 * and the one that drifts is always the one nobody is looking at — a nav entry
 * pointing at a route name that no longer exists navigates nowhere and says
 * nothing. Each page names its `view`; this is where a view becomes a screen.
 */
const VIEWS = {
    'collection': CollectionView,
    'humans': HumansView,
    'machines': MachinesView,
    'live-sales': LiveSalesView,
    'coming-soon': ComingSoonView
}

function propsFor(page) {
    switch (page.view) {
        case 'collection':
            return { collection: page.id, title: page.label, singular: page.singular }
        case 'humans':
        case 'machines':
            return { title: page.label, singular: page.singular }
        case 'coming-soon':
            return { title: page.label, icon: page.icon, blurb: page.blurb }
        default:
            return {}
    }
}

const routes = PAGES.map(function (page) {
    return {
        path: page.path,
        name: page.routeName,
        component: VIEWS[page.view],
        props: propsFor(page)
    }
})

/**
 * The clerk console, opened from a live lane on the Live Sales page and keyed
 * on the runtime's event id. It opens in its own tab: `meta.standalone` is
 * what App.vue reads to render it outside the shell; `meta.page` names the
 * page it belongs to, because the console is not a page in the nav.
 */
const clerkRoute = {
    path: '/operate/live-sales/:eventId/clerk',
    name: 'clerk',
    component: ClerkConsole,
    props: true,
    // Standalone: opened in its own tab from Live Sales, rendered outside the
    // shell (App.vue reads `meta.standalone`); the console fills the viewport.
    meta: { standalone: true, page: 'live-sales' }
}

/**
 * The identity screens, on the shared identity paths — the desktop bindings the
 * identity UI package ships, never a local component behind an identity path
 * (the shared-screen rule; test/unit/IdentityRoutesStandard.test.js holds it).
 * `meta.identity` is what App.vue reads to render them outside the shell: they
 * are one card on a full-viewport backdrop, not a page in the administration
 * layout.
 */
const identityRoutes = identityDesktopRoutes.map(function (route) {
    return { ...route, meta: { identity: true } }
})

/**
 * The shared shell's own pages — Profile and Settings — behind the App Menu's
 * doors. They carry no `meta`, so they render inside the administration layout,
 * under the top bar; they are not pages in the nav, so the breadcrumb is empty
 * and each brings its own header and back arrow.
 */
const router = createRouter({
    history: createWebHistory(),
    routes: [{ path: '/', redirect: { name: PAGES[0].routeName } }, ...routes, clerkRoute, ...commonUiRoutes, ...identityRoutes]
})

export default router
